import React, { useEffect, useState } from 'react';
import type { DayData, ProviderKey, RecoveryDay, TrainingItem } from './types';
import { ActivityAccordion } from './ActivityAccordion';
import { ActivityDetail } from './ActivityDetail';
import { Pill } from './Pill';
import { SleepDetail } from './SleepDetail';

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    setReduced(mq.matches);
    const handler = () => setReduced(mq.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return reduced;
}

function trainingTitle(it: TrainingItem): string {
  if (it.kind === 'catapult_session') return it.name;
  if (it.kind === 'garmin_daily') return 'Daily activity';
  return it.display_label;
}

function trainingProvider(it: TrainingItem): ProviderKey {
  if (it.kind === 'catapult_session') return 'catapult';
  if (it.kind === 'garmin_daily') return 'garmin';
  return it.source as ProviderKey;
}

function readinessColorClass(score: number): string {
  if (score >= 80) return 'cgm-stat-success-text';
  if (score >= 60) return 'cgm-stat-warning-text';
  return 'cgm-stat-error-text';
}

function DayPanelRecoveryRibbon({ recovery }: { recovery: RecoveryDay }) {
  const sleepHrs = recovery.sleep
    ? `${(recovery.sleep.total_minutes / 60).toFixed(1)}h`
    : null;
  const score = recovery.readiness?.score ?? null;
  return (
    <div className="grid grid-cols-4 gap-2 mb-4">
      <Pill label="Sleep" value={sleepHrs} />
      <Pill label="HRV"
            value={recovery.hrv ? `${Math.round(recovery.hrv.value)}` : null} />
      <Pill label="RHR"
            value={recovery.resting_hr ? `${recovery.resting_hr.value}` : null} />
      <Pill
        label="Readiness"
        value={score != null ? `${score}` : null}
        valueClass={score != null ? readinessColorClass(score) : undefined}
      />
    </div>
  );
}

interface Props {
  date: string;
  day: DayData;
  onClose: () => void;
}

export function DayPanel({ date, day, onClose }: Props) {
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const transitionStyle: React.CSSProperties = reduced
    ? {
        transition: 'opacity 100ms linear',
        opacity: mounted ? 1 : 0,
      }
    : {
        transition:
          'transform var(--motion-panel-duration) var(--motion-panel-easing), ' +
          'opacity var(--motion-panel-duration) var(--motion-panel-easing)',
        transform: mounted ? 'translateX(0)' : 'translateX(100%)',
        opacity: mounted ? 1 : 0,
      };

  return (
    <aside
      data-testid="day-panel"
      data-reduced-motion={reduced ? 'true' : 'false'}
      style={{
        position: 'fixed',
        top: 0, right: 0, bottom: 0,
        width: 'min(380px, 90vw)',
        zIndex: 50,
        ...transitionStyle,
      }}
      className="cgm-bg-primary border-l border-[var(--card-border)] p-4 overflow-y-auto"
    >
      <header className="flex items-baseline justify-between mb-4">
        <h2 className="text-lg font-semibold cgm-text-primary">{date}</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-sm cgm-text-muted hover:cgm-text-primary"
          aria-label="Close panel"
        >
          ✕
        </button>
      </header>
      <DayPanelRecoveryRibbon recovery={day.recovery} />
      <div className="space-y-2">
        {day.recovery.sleep && (
          <ActivityAccordion
            title="Sleep"
            source={day.recovery.sleep.source}
            provider={day.recovery.sleep.source as ProviderKey}
            accent="var(--channel-recovery)"
            defaultOpen={day.training.length === 0 && day.assessment.length === 0}
          >
            <SleepDetail sleep={day.recovery.sleep} />
          </ActivityAccordion>
        )}
        {day.training.map((it, i) => (
          <ActivityAccordion
            key={`t-${i}`}
            title={trainingTitle(it)}
            source={it.source}
            provider={trainingProvider(it)}
            defaultOpen={i === 0}
          >
            <ActivityDetail item={it} />
          </ActivityAccordion>
        ))}
        {day.assessment.map((it, i) => (
          <ActivityAccordion
            key={`a-${i}`}
            title={it.test_type}
            source={it.source}
            provider="hawkin"
            accent="var(--channel-assessment)"
            defaultOpen={day.training.length === 0 && i === 0}
          >
            <ActivityDetail item={it} />
          </ActivityAccordion>
        ))}
      </div>
    </aside>
  );
}
